import { useEffect, useMemo, useState } from 'react'

import {
  Divider,
  Loader,
  Drawer,
  ActionIcon,
  Tooltip,
  Menu,
  TextInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import { CollapsibleSection } from 'src/components/CollapsibleSection'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import {
  GET_JOB_LISTING,
  UPDATE_JOB_LISTING,
  DELETE_JOB_LISTING,
} from 'src/graphql/joblistings.graphql'
import {
  GET_TRACKING_LINKS,
  CREATE_TRACKING_LINK,
  UPDATE_TRACKING_LINK,
  DELETE_TRACKING_LINK,
} from 'src/graphql/trackinglinks.graphql'
import { EmployeeStatus } from 'src/graphql/types/employees'
import { JobListingType } from 'src/graphql/types/jobListings'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import { type JobListingFieldValue } from 'src/lib/jobListings.utils'
import IconAdd from 'src/icons/IconAdd'
import IconSearch from 'src/icons/IconSearch'

import JobListingHeader from './JobListingHeader'
import JobListingOverview from './JobListingOverview'
import JobListingTrackingLinks from './JobListingTrackingLinks'
import JobQuestionsSection from './JobQuestionsSection'

type PendingChanges = {
  title?: string
  address?: Record<string, unknown> | null
  jobPostingDate?: Date | string | null
  type?: string | null
  recruiters?: string[]
  memberEmployeeIds?: string[]
  jobListingTemplateId?: string | null
  subtitle?: string | null
  description?: string | null
  benefits?: string[]
  employmentType?: string | null
  payMin?: number | null
  payMax?: number | null
  payUnit?: string | null
  licenseClass?: string[]
  endorsements?: string[]
  minMonthsExperience?: number | null
  minEmploymentHistoryYears?: number | null
}

const JobListingDetailsDrawer = ({ jobListingId, onClose, onJobListingUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [openedDelete, deleteHandlers] = useDisclosure(false)
  const [openedDeleteJob, deleteJobHandlers] = useDisclosure(false)
  const [selectedLink, setSelectedLink] = useState()
  const [selectedLinkDetails, setSelectedLinkDetails] = useState(null)
  const [trackingLinksOpen, setTrackingLinksOpen] = useState(true)
  const [selectedLeadEmployees, setSelectedLeadEmployees] = useState([])
  const [selectedMemberEmployees, setSelectedMemberEmployees] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({})
  const [linkTypeSearch, setLinkTypeSearch] = useState('')

  const { currentUser } = useAuth()

  /**********************    QUERIES    **********************/
  const {
    data: jobData,
    loading: jobLoading,
    refetch: refetchJobDetails,
  } = useQuery(GET_JOB_LISTING, {
    variables: { id: jobListingId },
    skip: !jobListingId,
  })

  const job = jobData?.jobListing

  // Fetch all company employees for tracking link assignment
  // Admins can assign both ADMIN and RECRUITER employees
  // Recruiters can only assign RECRUITER employees
  const isAdmin = currentUser?.roles?.includes('ADMIN')
  const roleTypesFilter = isAdmin ? ['ADMIN', 'RECRUITER'] : ['RECRUITER']

  const { data: allEmployeesData } = useQuery(GET_EMPLOYEES_ONLY, {
    variables: {
      pageInfo: { offset: 0, limit: 100 },
      filters: { roleTypes: roleTypesFilter },
    },
    fetchPolicy: 'cache-and-network',
  })

  const allEmployeeOptions = useMemo(() => {
    return (
      allEmployeesData?.employees?.items?.map((employee) => {
        const name = `${employee.firstName} ${employee.lastName}`
        const isInactive = employee.status !== EmployeeStatus.ACTIVE
        const isAdminRole = employee.roleType === 'ADMIN'

        let label = name
        if (isAdminRole) {
          label = `${name} (Admin)`
        }
        if (isInactive) {
          label = isAdminRole
            ? `${name} (Admin, Inactive)`
            : `${name} (Inactive)`
        }

        return {
          employeeId: employee.employeeId,
          label,
          status: employee.status,
        }
      }) ?? []
    )
  }, [allEmployeesData])

  useEffect(() => {
    // Don't clobber in-progress recruiter edits when the job listing
    // refreshes (e.g. after adding a tracking link).
    if (isEditMode) {
      return
    }
    if (job?.employees) {
      setSelectedLeadEmployees(
        job.employees.map((employee) => ({
          label: `${employee.firstName} ${employee.lastName}`,
          value: employee.employeeId,
          status: employee.status,
        }))
      )
    } else {
      setSelectedLeadEmployees([])
    }
  }, [job?.employees, isEditMode])

  useEffect(() => {
    if (isEditMode) {
      return
    }
    if (job?.memberEmployees) {
      setSelectedMemberEmployees(
        job.memberEmployees.map((employee) => ({
          label: `${employee.firstName} ${employee.lastName}`,
          value: employee.employeeId,
          status: employee.status,
        }))
      )
    } else {
      setSelectedMemberEmployees([])
    }
  }, [job?.memberEmployees, isEditMode])

  const {
    data,
    loading: trackingLinksLoading,
    error: trackingLinksError,
  } = useQuery(GET_TRACKING_LINKS, {
    variables: { jobListingId },
    skip: !jobListingId,
  })

  // Log any GraphQL errors for debugging
  if (trackingLinksError) {
    console.error('Error fetching tracking links:', trackingLinksError)
  }

  const trackingLinks = trackingLinksLoading
    ? []
    : data?.trackingLinks?.items ?? []

  /**********************    MUTATIONS    **********************/
  const [updateJobListing] = useMutation(UPDATE_JOB_LISTING, {
    onError: () => {
      setIsSaving(false)
      toast('Unable to update the job listing, please try again.', 'error', {
        jobListingId,
        operation: 'updateJobListing',
      })
    },
  })

  const [createTrackingLink] = useMutation(CREATE_TRACKING_LINK, {
    refetchQueries: [
      {
        query: GET_TRACKING_LINKS,
        variables: { jobListingId },
      },
    ],
    onCompleted: () => {
      toast('Link created successfully', 'success')
    },
    onError: () => {
      toast('Unable to create link, please try again.', 'error')
    },
  })

  const [updateTrackingLink] = useMutation(UPDATE_TRACKING_LINK, {
    refetchQueries: [
      {
        query: GET_TRACKING_LINKS,
        variables: { jobListingId },
      },
    ],
    onCompleted: () => {
      toast('Link updated successfully', 'success')
    },
  })

  const [deleteTrackingLink] = useMutation(DELETE_TRACKING_LINK, {
    variables: {
      id: selectedLink,
    },
    refetchQueries: [
      {
        query: GET_TRACKING_LINKS,
        variables: { jobListingId },
      },
    ],
    onCompleted: () => {
      toast('Link deleted successfully', 'success')
    },
    onError: () => {
      toast('Unable to delete link, please try again.', 'error')
    },
  })

  const [deleteJobListing] = useMutation(DELETE_JOB_LISTING, {
    onCompleted: () => {
      toast('Job listing deleted successfully', 'success')
      // Refetch the parent list to update the table
      if (onJobListingUpdate) {
        onJobListingUpdate()
      }
      onClose()
    },
    onError: () => {
      toast('Unable to delete job listing, please try again.', 'error')
    },
  })

  /**********************    FUNCTIONS    **********************/
  const handleToggleEdit = () => {
    if (isEditMode) {
      // If canceling, reset to original values
      if (job?.employees) {
        setSelectedLeadEmployees(
          job.employees.map((employee) => ({
            label: `${employee.firstName} ${employee.lastName}`,
            value: employee.employeeId,
            status: employee.status,
          }))
        )
      } else {
        setSelectedLeadEmployees([])
      }
      if (job?.memberEmployees) {
        setSelectedMemberEmployees(
          job.memberEmployees.map((employee) => ({
            label: `${employee.firstName} ${employee.lastName}`,
            value: employee.employeeId,
            status: employee.status,
          }))
        )
      } else {
        setSelectedMemberEmployees([])
      }
      // Clear pending changes when canceling
      setPendingChanges({})
    }
    setIsEditMode(!isEditMode)
  }

  const handleFieldChange = (field: string, value: JobListingFieldValue) => {
    setPendingChanges((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Validation logic
  const isValidForSave = () => {
    // Get current title (from pending changes or original job)
    const currentTitle =
      pendingChanges.title !== undefined
        ? pendingChanges.title
        : job?.title || ''

    // Get current recruiters (from pending changes or selected recruiters)
    const currentRecruiters =
      pendingChanges.recruiters !== undefined
        ? pendingChanges.recruiters
        : selectedLeadEmployees.map((r) => r.value)

    // Title must have at least 1 character (trimmed)
    const hasTitleValue = currentTitle.trim().length > 0

    // Must have at least one assigned employee
    const hasAssignedEmployees = currentRecruiters.length > 0

    const payEdited =
      pendingChanges.payMin !== undefined || pendingChanges.payMax !== undefined
    const effectivePayMin =
      pendingChanges.payMin !== undefined ? pendingChanges.payMin : job?.payMin
    const effectivePayMax =
      pendingChanges.payMax !== undefined ? pendingChanges.payMax : job?.payMax
    const hasValidPayRange = !(
      payEdited &&
      effectivePayMin != null &&
      effectivePayMax != null &&
      effectivePayMax < effectivePayMin
    )

    return hasTitleValue && hasAssignedEmployees && hasValidPayRange
  }

  const hasPendingChanges = Object.keys(pendingChanges).length > 0

  const buildUpdateInput = (): PendingChanges => {
    const updateInput: PendingChanges = {}

    if (pendingChanges.title !== undefined) {
      updateInput.title = pendingChanges.title
    }

    if (pendingChanges.address !== undefined) {
      updateInput.address = pendingChanges.address
    }

    if (pendingChanges.jobPostingDate !== undefined) {
      updateInput.jobPostingDate =
        pendingChanges.jobPostingDate instanceof Date
          ? pendingChanges.jobPostingDate.toISOString()
          : pendingChanges.jobPostingDate
    }

    if (pendingChanges.type !== undefined) {
      updateInput.type = pendingChanges.type
    }

    if (pendingChanges.recruiters !== undefined) {
      updateInput.recruiters = pendingChanges.recruiters
    }

    if (pendingChanges.memberEmployeeIds !== undefined) {
      updateInput.memberEmployeeIds = pendingChanges.memberEmployeeIds
    }

    if (pendingChanges.jobListingTemplateId !== undefined) {
      updateInput.jobListingTemplateId = pendingChanges.jobListingTemplateId
    }

    if (pendingChanges.subtitle !== undefined) {
      updateInput.subtitle = pendingChanges.subtitle
    }
    if (pendingChanges.description !== undefined) {
      updateInput.description = pendingChanges.description
    }
    if (pendingChanges.benefits !== undefined) {
      updateInput.benefits = pendingChanges.benefits
    }
    if (pendingChanges.employmentType !== undefined) {
      updateInput.employmentType = pendingChanges.employmentType
    }
    if (pendingChanges.payMin !== undefined) {
      updateInput.payMin = pendingChanges.payMin
    }
    if (pendingChanges.payMax !== undefined) {
      updateInput.payMax = pendingChanges.payMax
    }
    if (pendingChanges.payUnit !== undefined) {
      updateInput.payUnit = pendingChanges.payUnit
    }
    if (pendingChanges.licenseClass !== undefined) {
      updateInput.licenseClass = pendingChanges.licenseClass
    }
    if (pendingChanges.endorsements !== undefined) {
      updateInput.endorsements = pendingChanges.endorsements
    }
    if (pendingChanges.minMonthsExperience !== undefined) {
      updateInput.minMonthsExperience = pendingChanges.minMonthsExperience
    }
    if (pendingChanges.minEmploymentHistoryYears !== undefined) {
      updateInput.minEmploymentHistoryYears =
        pendingChanges.minEmploymentHistoryYears
    }

    const effectiveType =
      pendingChanges.type !== undefined ? pendingChanges.type : job?.type
    if (effectiveType === JobListingType.OFFICE) {
      updateInput.licenseClass = []
      updateInput.endorsements = []
      updateInput.minMonthsExperience = null
    }

    return updateInput
  }

  const saveJobListing = async (options?: {
    keepEditMode?: boolean
  }): Promise<boolean> => {
    if (!isValidForSave()) {
      return false
    }

    setIsSaving(true)

    try {
      await updateJobListing({
        variables: {
          id: jobListingId,
          input: buildUpdateInput(),
        },
      })
      refetchJobDetails()
      setIsSaving(false)
      if (!options?.keepEditMode) {
        setIsEditMode(false)
      }
      setPendingChanges({})
      toast('Job listing updated successfully', 'success')
      if (onJobListingUpdate) {
        onJobListingUpdate()
      }
      return true
    } catch {
      return false
    }
  }

  const handleSave = () => {
    void saveJobListing()
  }

  const createLink = (values: { linkType: string; linkName: string }) => {
    createTrackingLink({
      variables: {
        input: {
          linkType: values.linkType,
          linkName: values.linkName,
          jobListingId,
        },
      },
    })
  }

  const handleCreateLink = async (values: {
    linkType: string
    linkName: string
  }) => {
    if (!jobListingId || isSaving) {
      return
    }

    // Persist in-progress edits before adding a tracking link so a refresh
    // of the job listing does not wipe the user's work.
    if (isEditMode && hasPendingChanges) {
      if (!isValidForSave()) {
        toast(
          'Please fix form errors before adding a tracking link.',
          'error'
        )
        return
      }

      const saved = await saveJobListing({ keepEditMode: true })
      if (!saved) {
        return
      }
    }

    createLink(values)
  }

  const handleDeleteLink = (linkId) => {
    const linkToDelete = trackingLinks.find(
      (link) => link.trackingLinkId === linkId
    )
    setSelectedLink(linkId)
    setSelectedLinkDetails(linkToDelete)
    deleteHandlers.open()
  }

  const handleDeleteJob = () => {
    deleteJobHandlers.open()
  }

  const handleUpdateLinkName = (trackingLinkId: string, linkName: string) => {
    updateTrackingLink({
      variables: {
        id: trackingLinkId,
        input: {
          linkName: linkName || null,
        },
      },
      onError: () => {
        toast('Unable to update link, please try again.', 'error')
      },
    })
  }

  const handleDeleteJobConfirm = () => {
    deleteJobListing({
      variables: {
        id: jobListingId,
      },
    })
  }

  return (
    <Drawer
      opened={true}
      onClose={onClose}
      position="right"
      withCloseButton={false}
      size={'30%'}
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        body: 'flex flex-col gap-10 h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        <DeleteModal
          title="Delete Tracking Link"
          description={
            selectedLinkDetails
              ? `Are you sure you want to delete the tracking link ${LinkTypeDisplay[selectedLinkDetails.linkType] ||
              selectedLinkDetails.linkType
              }${selectedLinkDetails.linkName?.trim()
                ? ` "${selectedLinkDetails.linkName.trim()}"`
                : ''
              }?`
              : 'Are you sure you want to delete this tracking link?'
          }
          opened={openedDelete}
          close={deleteHandlers.close}
          onDelete={deleteTrackingLink}
        />

        <DeleteModal
          title="Delete Job Listing"
          description={`Are you sure you want to delete "${job?.title}"? This action cannot be undone.`}
          opened={openedDeleteJob}
          close={deleteJobHandlers.close}
          onDelete={handleDeleteJobConfirm}
        />

        {jobLoading && !job ? (
          <div className="flex h-full items-center justify-center">
            <Loader color="gray" />
          </div>
        ) : (
          <>
            <div className="flex h-full flex-1 flex-col gap-3 overflow-y-auto">
              <JobListingHeader
                title={job?.title || 'Job Listing'}
                isEditMode={isEditMode}
                onToggleEdit={handleToggleEdit}
                onSave={handleSave}
                onDelete={handleDeleteJob}
                isSaving={isSaving}
                canSave={isValidForSave()}
              />
              <Divider variant="dashed" />

              <div className="flex flex-col gap-3 overflow-y-auto">
                <JobListingOverview
                  job={job}
                  isEditMode={isEditMode}
                  selectedLeadEmployees={selectedLeadEmployees}
                  setSelectedLeadEmployees={setSelectedLeadEmployees}
                  selectedMemberEmployees={selectedMemberEmployees}
                  setSelectedMemberEmployees={setSelectedMemberEmployees}
                  onFieldChange={handleFieldChange}
                />

                <Divider variant="dashed" />

                <CollapsibleSection
                  title="TRACKING LINKS"
                  open={trackingLinksOpen}
                  onOpenChange={setTrackingLinksOpen}
                  rightSection={
                    isEditMode && (
                      <Menu
                        shadow="md"
                        width={200}
                        position="bottom-end"
                        withinPortal={false}
                        disabled={isSaving}
                      >
                        <Menu.Target>
                          <Tooltip label="Add tracking link">
                            <ActionIcon
                              size={20}
                              variant="subtle"
                              color="gray"
                              disabled={isSaving}
                              aria-label="Add tracking link"
                            >
                              <IconAdd className="h-4 fill-none stroke-doubleNickel-gray-700" />
                            </ActionIcon>
                          </Tooltip>
                        </Menu.Target>

                        <Menu.Dropdown style={{ maxHeight: 300 }}>
                          <div
                            className="sticky top-0 z-10 bg-white p-2"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <TextInput
                              placeholder="Search link types..."
                              value={linkTypeSearch}
                              onChange={(e) =>
                                setLinkTypeSearch(e.target.value)
                              }
                              leftSection={
                                <IconSearch className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
                              }
                              size="xs"
                            />
                          </div>
                          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                            {Object.entries(LinkTypeDisplay)
                              .filter(([value, label]) =>
                                label
                                  .toLowerCase()
                                  .includes(linkTypeSearch.toLowerCase())
                              )
                              .sort(([, labelA], [, labelB]) =>
                                labelA.localeCompare(labelB)
                              )
                              .map(([value, label]) => (
                                <Menu.Item
                                  key={value}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCreateLink({
                                      linkType: value,
                                      linkName: '',
                                    })
                                    setLinkTypeSearch('')
                                  }}
                                >
                                  {label}
                                </Menu.Item>
                              ))}
                          </div>
                        </Menu.Dropdown>
                      </Menu>
                    )
                  }
                >
                  <JobListingTrackingLinks
                    trackingLinks={trackingLinks}
                    trackingLinksLoading={trackingLinksLoading}
                    isEditMode={isEditMode}
                    jobListingId={jobListingId}
                    onDeleteLink={handleDeleteLink}
                    onUpdateLinkName={handleUpdateLinkName}
                    allEmployeeOptions={allEmployeeOptions}
                    jobDescription={job?.description}
                    jobEmploymentType={job?.employmentType}
                    jobPayMin={job?.payMin}
                    jobPayUnit={job?.payUnit}
                  />
                </CollapsibleSection>

                <Divider variant="dashed" />

                <AdminContainer>
                  <CollapsibleSection title="JOB QUESTIONS" defaultOpen={false}>
                    <JobQuestionsSection jobListingId={jobListingId} />
                  </CollapsibleSection>
                </AdminContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}

export default JobListingDetailsDrawer
